import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

interface HelpDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function HelpDialog({ open, onOpenChange }: HelpDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">
                        Code Manager Microservice
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 text-sm text-gray-700 leading-relaxed">

                    <h2 className="text-lg font-semibold text-black">Type Manager</h2>

                    {/* CREATE TYPE */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Creating a Type</h3>
                        <p>
                            A Type acts as a parent entity. Users can create a Type by providing:
                        </p>
                        <ul className="list-disc ml-5 space-y-0.5">
                            <li>Type Name and Description.</li>
                        </ul>

                        <div>
                            <p className="font-semibold text-black">Child Types</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Each Type can have one or more Child Types or Child Codes.</li>
                                <li>Each Child Type can have one or more Child Codes.</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-semibold text-black">Ways to Create a Type</p>
                            <p className="mt-1">Types can be created in two ways:</p>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">Manual Creation</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Navigate to the Type Manager.</li>
                                <li>Click the <span className="font-medium">Create Type</span> button.</li>
                                <li>Enter the required details and save.</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">AI / NLPS Console</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Data types can also be created using natural language commands through the AI/NLPS Console.</li>
                            </ul>
                        </div>

                        <div className="bg-gray-100 p-3 rounded text-xs space-y-1">
                            <p className="font-semibold mb-1">Example Commands</p>
                            <p>Create a type named XYZ</p>
                            <p>Create types named XYZ, ABC, PQR</p>
                        </div>
                    </section>

                    {/* CODE MANAGER */}
                    <h2 className="text-lg font-semibold text-black">Code Manager</h2>

                    {/* CREATE CODES */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Creating Codes</h3>
                        <p>
                            Codes can only be created within a Parent Type or Child Type. Users can create
                            codes by providing:
                        </p>
                        <ul className="list-disc ml-5 space-y-0.5">
                            <li>Code Name and Description.</li>
                        </ul>

                        <div>
                            <p className="font-semibold text-black">Ways to Create Codes</p>
                            <p className="mt-1">Codes can be created using the following methods:</p>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">Manual Creation</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Click the <span className="font-medium">Plus (+) icon</span> in the Action column on the right side of the Type name in the Code Manager section.</li>
                                <li>Enter the required details and save.</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">AI / NLPS Console</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Codes can also be created using natural language commands.</li>
                            </ul>
                        </div>

                        <div className="bg-gray-100 p-3 rounded text-xs space-y-1">
                            <p className="font-semibold mb-1">Example Commands</p>
                            <p>Create a code named AB within the type XYZ.</p>
                            <p>Create codes named AB, CD, EF, GH within the type XYZ.</p>
                        </div>
                    </section>

                    {/* DATA QUALITY */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Data Quality</h3>
                        <p>The Data Quality module identifies and logs the following three types of issues:</p>
                        <ul className="space-y-2 ml-5">
                            <li>
                                <span className="font-semibold">Duplicate</span> – Identifies rows with the same Type and exactly the same Code Name.
                            </li>
                            <li>
                                <span className="font-semibold">Orphans</span> – Identifies codes that do not have a valid parent type reference.
                            </li>
                            <li>
                                <span className="font-semibold">Overlaps</span> – Identifies codes with the same name that exist under different types.
                            </li>
                        </ul>
                    </section>

                    {/* IMPORT / EXPORT */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Import / Export</h3>

                        <div>
                            <p className="font-semibold">Import</p>
                            <p className="mt-1">Types and codes can be added by importing files.</p>
                            <p className="mt-1 font-medium">Supported file formats:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>JSON</li>
                                <li>CSV</li>
                                <li>Word</li>
                                <li>PDF</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">Export</p>
                            <p className="mt-1">Data can be exported in different formats and scopes.</p>
                            <p className="mt-1 font-medium">Available export scopes:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Full Dataset</li>
                                <li>Types Only</li>
                                <li>Codes Only</li>
                            </ul>
                        </div>
                    </section>

                    {/* AUDIT LOG */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Audit Log</h3>
                        <p>The Audit Log records all system errors related to the Code Manager, including:</p>
                        <ul className="list-disc ml-5 space-y-0.5">
                            <li>Validation errors</li>
                            <li>Authentication errors</li>
                            <li>Database errors</li>
                        </ul>
                        <div>
                            <p className="font-medium">Each log entry includes:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>IP address</li>
                                <li>Status code</li>
                                <li>Error timestamp</li>
                                <li>Error type</li>
                            </ul>
                        </div>
                    </section>

                    {/* AI/NLPS CONSOLE */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">AI/NLPS Console</h3>
                        <p>
                            The AI Console allows users to add, edit, retrieve, and delete types and codes.
                            The system supports adding multiple items in a single operation.
                        </p>

                        <div className="bg-gray-100 p-3 rounded text-xs space-y-1 max-h-64 overflow-y-auto">
                            <p className="font-semibold mb-2">Example Commands</p>
                            <p>Add types named UKJ, RED, KIL</p>
                            <p>Add codes named HG, JK, JJ within type UKJ</p>
                            <p>Add child types named BLDF, TREW, QAWS, ERTS within type RED</p>
                            <p>Delete Type named KIL</p>
                            <p>Add types named QAW, RBV</p>
                            <p>Add codes named LSD, TYE within type QAW</p>
                            <p>Add codes named ukld, kjhr, fgre within type RBV</p>
                        </div>
                    </section>

                </div>
            </DialogContent>
        </Dialog>
    );
}